import { datadogRum } from "@datadog/browser-rum";

import { isKnownScopeViewType } from "./scopeViewType";

/**
 * Drops rows whose `scope_view` this build of the app does not recognize.
 *
 * Why this exists: data always deploys before the app. `nira-deployments`
 * chains the three repos with `needs:` — hasura, then nira-data-pipeline
 * (which commits the prospecting upload), then nira-app — so between the
 * pipeline finishing and the app going live there is a window where
 * `constraint_stacks` carries a scope view this build has never heard of.
 * `convertToScopeViewType` throws on one, and the app has no error boundary,
 * so the throw unmounts the whole React tree: a blank page rather than a
 * missing row. Observed as the ERCOT 26SSWG `sp_pnl_discharging_hrml_charging`
 * rollout risk.
 *
 * Why dropping the row is safe rather than merely quieter: every consumer of
 * these rows already selects by an exact, registered scope view — the bus
 * page, the constraints pane and the cost graphs filter `scope_view` in SQL,
 * and the curtailment bus pane and line-tap pane filter in JS on
 * `constraint.scopeView === scopeView`. A view the app cannot name can never
 * equal a view it asked for, and `ScopeViewConfig` is typed against
 * `ScopeViewType`, so the app cannot ask for an unregistered one in the first
 * place. Dropping the row therefore moves no number and shortens no list a
 * user could otherwise have seen; it only removes the crash.
 *
 * The drop is still reported, so an unknown view that outlives a deploy window
 * — a genuine registration bug rather than a deploy skew — is visible to us
 * instead of silent.
 */
export const withKnownScopeViews = <T extends { readonly scope_view: string }>(
    rows: ReadonlyArray<T>,
    source: string
): ReadonlyArray<T> => {
    const known = rows.filter((row) => isKnownScopeViewType(row.scope_view));

    if (known.length !== rows.length) {
        reportUnknownScopeViews(rows, known.length, source);
    }

    return known;
};

const reportUnknownScopeViews = <T extends { readonly scope_view: string }>(
    rows: ReadonlyArray<T>,
    keptCount: number,
    source: string
): void => {
    const unknownScopeViews = Array.from(
        new Set(
            rows
                .filter((row) => !isKnownScopeViewType(row.scope_view))
                .map((row) => row.scope_view)
        )
    );

    datadogRum.addError(
        new Error(
            `Dropped ${rows.length - keptCount} row(s) with unregistered ` +
                `scope_view in ${source}: ${unknownScopeViews.join(", ")}`
        ),
        { source, unknownScopeViews, droppedCount: rows.length - keptCount }
    );
};
